/**
 * EstimatorNewPage — 3-step wizard for the Project Conditions flow (Batch B).
 *
 * Step 1: Customer + Project basics (org, name, description, audience, hosting)
 * Step 2: Project Conditions (dates, breaks, blackouts, hypercare, holiday region)
 * Step 3: Summary → hands off to /estimator with the selections stashed in
 *          sessionStorage. EstimatorPage picks them up on mount.
 *
 * Once Batch C ships, estimates created via this flow have `has_conditions=True`
 * and the Planner becomes a read-only derived view.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, ChevronRight, ChevronLeft, Check, Sparkles,
  CalendarDays, Server, Cloud, Users, Package,
} from 'lucide-react';
import { ProductPricingCalculator } from '../components/product-pricing/ProductPricingCalculator';
import { cleanInputs } from '../components/product-pricing/useProductPricing';
import {
  ProjectConditionsForm, EMPTY_CONDITIONS,
} from '../components/estimator/ProjectConditionsForm';
import { suggestConditionsFromBand } from '../lib/maturityDefaults';
import PageHelpButton from '../components/PageHelpButton';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STASH_KEY = 'pendingNewEstimateWizardV1';

const STEPS = [
  { id: 1, label: 'Customer', desc: 'Who is this estimate for?' },
  // v260.117 — Optional Maturity step. Placed between customer pick and
  // project conditions so the rep can score (or skip) the org's
  // baseline before locking dates and constraints.
  { id: 2, label: 'Maturity', desc: 'Score the customer (optional)' },
  { id: 3, label: 'Conditions', desc: 'Set the project constraints' },
  { id: 4, label: 'Product Pricing', desc: 'Subscription & TCO (optional)' },
  { id: 5, label: 'Review', desc: 'Confirm & continue' },
];

export default function EstimatorNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [productPricing, setProductPricing] = useState({ inputs: null, preview: null });
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  // Step 1 state
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [implName, setImplName] = useState('');
  const [implDescription, setImplDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [hostingModel, setHostingModel] = useState('public_cloud');

  // Step 2 state
  const [conditions, setConditions] = useState(EMPTY_CONDITIONS);
  const [wizCloseDate, setWizCloseDate] = useState(null);
  const [salesLead, setSalesLead] = useState(null);
  const [csLead, setCsLead] = useState(null);
  // v378 — Entity & Region selection at creation (via Project Conditions).
  const [wizEntities, setWizEntities] = useState([]);
  const [wizEntityId, setWizEntityId] = useState(null);
  const [wizRegion, setWizRegion] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/seertech-entities`, { withCredentials: true })
      .then(res => {
        const list = res.data?.entities || [];
        setWizEntities(list);
        // v379 — home entity/region defaults for new estimates.
        const pref = user?.preferences || {};
        if (pref.default_contracting_entity_id && list.some(e => e.id === pref.default_contracting_entity_id)) {
          setWizEntityId(prev => prev ?? pref.default_contracting_entity_id);
          if (pref.default_region) setWizRegion(prev => prev ?? pref.default_region);
        }
      })
      .catch(() => setWizEntities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  // v260.118 — Maturity insights for the picked org are lifted to the
  // parent so the wizard can (a) display them on Step 2 read-only and
  // (b) seed sensible defaults into Project Conditions when advancing
  // to Step 3 (provided the user hasn't already touched conditions).
  const [maturityInsights, setMaturityInsights] = useState(null);
  // Track which condition fields were auto-suggested so the rep can
  // distinguish "I set this" from "system suggested this".
  const [autoSuggested, setAutoSuggested] = useState({ duration: false, hypercare: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/organizations`, { withCredentials: true });
        if (!cancelled) setOrganizations(res.data.organizations || []);
      } catch (e) {
        if (!cancelled) toast.error('Could not load organizations');
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-select org when arriving via deep-link (e.g. ⌘K palette → "New estimate for…")
  useEffect(() => {
    const orgId = searchParams.get('org_id');
    if (!orgId || organizations.length === 0) return;
    const match = organizations.find((o) => o.org_id === orgId);
    if (match && !selectedOrgId) {
      setSelectedOrgId(orgId);
      setOrgName(match.name || '');
    }
  }, [searchParams, organizations, selectedOrgId]);

  const onPickOrg = (orgId) => {
    setSelectedOrgId(orgId);
    const match = organizations.find((o) => o.org_id === orgId);
    if (match) setOrgName(match.name || '');
  };

  const canAdvanceStep1 = useMemo(() => {
    return Boolean((orgName || '').trim() && (implName || '').trim());
  }, [orgName, implName]);

  const canAdvanceStep2 = useMemo(() => {
    // Conditions are optional individually, but at least ONE meaningful field
    // must be set or the user is essentially still on legacy flow.
    const c = conditions;
    return Boolean(
      c.goal_duration_weeks
      || c.goal_start_date
      || c.goal_go_live_date
      || c.hypercare_weeks
      || (c.breaks && c.breaks.length > 0)
      || (c.blackouts && c.blackouts.length > 0)
      || c.holiday_region,
    );
  }, [conditions]);

  const handleContinue = () => {
    if (step === 1 && !canAdvanceStep1) {
      toast.error('Please set a customer and project name');
      return;
    }
    // v260.118 — When advancing from Step 2 (Maturity) to Step 3
    // (Conditions), seed duration + hypercare from the band IF the user
    // hasn't already typed anything into the conditions form. We never
    // overwrite a hand-set value; the user always wins.
    if (step === 2 && maturityInsights?.has_assessment) {
      const suggestion = suggestConditionsFromBand(maturityInsights.maturity_band);
      if (suggestion) {
        const patch = {};
        const newSuggested = { ...autoSuggested };
        if (!conditions.goal_duration_weeks) {
          patch.goal_duration_weeks = suggestion.goal_duration_weeks;
          newSuggested.duration = true;
        }
        if (!conditions.hypercare_weeks) {
          patch.hypercare_weeks = suggestion.hypercare_weeks;
          newSuggested.hypercare = true;
        }
        if (Object.keys(patch).length > 0) {
          setConditions({ ...conditions, ...patch });
          setAutoSuggested(newSuggested);
        }
      }
    }
    // Step 2 (Maturity) is intentionally optional — no advance guard.
    if (step === 3 && wizEntities.length > 0) {
      const en = wizEntities.find(x => x.id === wizEntityId);
      const regions = en ? (en.regions?.length ? en.regions : (en.region ? [en.region] : [])) : [];
      if (!en) {
        toast.error('Choose the Selling Entity first — it sets the price book used for pricing.');
        return;
      }
      if (regions.length > 1 && !wizRegion) {
        toast.error(`Pick the region for ${en.code || en.name} before continuing.`);
        return;
      }
    }
    if (step === 3 && !canAdvanceStep2) {
      toast.error('Set at least one condition (e.g. goal duration) — or you should use the legacy /estimator flow.');
      return;
    }
    if (step < 5) {
      setStep(step + 1);
      return;
    }
    // Step 5 → finalize and navigate
    if (wizEntities.length > 0 && !wizEntityId) {
      toast.error('Choose the Selling Entity on the Conditions step before creating the estimate.');
      setStep(3);
      return;
    }
    const payload = {
      org_id: selectedOrgId || null,
      organization_name: (orgName || '').trim(),
      implementation_name: (implName || '').trim(),
      implementation_description: (implDescription || '').trim(),
      target_audience: targetAudience ? parseInt(targetAudience, 10) : null,
      hosting_model: hostingModel,
      project_conditions: { ...conditions },
      has_conditions: true,
      sales_lead: salesLead || null,
      cs_lead: csLead || null,
      contracting_entity_id: wizEntityId || null,
      region: wizRegion || null,
      deal_close_date: wizCloseDate || null,
      product_pricing_inputs: productPricing.inputs && Number(productPricing.inputs.quantities?.active_users) > 0 ? cleanInputs(productPricing.inputs) : null,
      created_via: 'wizard_v1',
      stashed_at: new Date().toISOString(),
    };
    try {
      sessionStorage.setItem(STASH_KEY, JSON.stringify(payload));
    } catch (e) {
      toast.error('Browser storage unavailable — falling back to URL state');
    }
    const params = new URLSearchParams();
    if (selectedOrgId) params.set('org_id', selectedOrgId);
    params.set('from_wizard', '1');
    navigate(`/estimator?${params.toString()}`);
  };

  const handleBack = () => {
    if (step === 1) navigate('/workspaces');
    else setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="estimator-new-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-8">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#4ABFCF] uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            New Estimate · Conditions Flow
          </div>
          <h1 className="text-3xl font-bold text-[#0F2040] mt-2 flex items-center gap-2">
            Set the project conditions first
            <PageHelpButton section="2-creating-your-first-estimate" label="New Estimate — open the user guide" />
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Tell us the customer and the constraints (duration goal, key dates, breaks, blackouts).
            We'll honor those as the source of truth — your Planner becomes a read-only view derived from them.
          </p>
        </header>

        <StepIndicator step={step} />

        <div className="mt-8">
          {step === 1 && (
            <Step1Customer
              organizations={organizations}
              orgsLoading={orgsLoading}
              selectedOrgId={selectedOrgId}
              onPickOrg={onPickOrg}
              orgName={orgName}
              setOrgName={setOrgName}
              implName={implName}
              setImplName={setImplName}
              implDescription={implDescription}
              setImplDescription={setImplDescription}
              targetAudience={targetAudience}
              setTargetAudience={setTargetAudience}
              hostingModel={hostingModel}
              setHostingModel={setHostingModel}
            />
          )}
          {step === 2 && (
            <Step2Maturity
              orgId={selectedOrgId}
              orgName={orgName}
              onInsights={setMaturityInsights}
            />
          )}
          {step === 3 && (
            <>
              {(autoSuggested.duration || autoSuggested.hypercare) && maturityInsights?.has_assessment && (
                <div className="mb-3 flex items-start gap-3 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2.5" data-testid="conditions-maturity-suggestion-banner">
                  <Sparkles className="w-4 h-4 text-cyan-700 mt-0.5 shrink-0" />
                  <div className="flex-1 text-sm text-cyan-900">
                    <b>Suggested from {maturityInsights.maturity_band} maturity</b> · We pre-filled
                    {autoSuggested.duration ? <> a <b>{conditions.goal_duration_weeks}-week</b> duration</> : null}
                    {autoSuggested.duration && autoSuggested.hypercare ? ' and ' : null}
                    {autoSuggested.hypercare ? <> an <b>{conditions.hypercare_weeks}-week</b> hypercare window</> : null}
                    {' '}for this band. Feel free to override below.
                  </div>
                  <button
                    type="button"
                    className="text-xs text-cyan-700 hover:text-cyan-900 underline shrink-0"
                    onClick={() => {
                      const patch = {};
                      if (autoSuggested.duration) patch.goal_duration_weeks = null;
                      if (autoSuggested.hypercare) patch.hypercare_weeks = null;
                      setConditions({ ...conditions, ...patch });
                      setAutoSuggested({ duration: false, hypercare: false });
                    }}
                    data-testid="conditions-maturity-suggestion-clear"
                  >
                    Clear suggestion
                  </button>
                </div>
              )}
              <ProjectConditionsForm
                value={conditions}
                onChange={(next) => {
                  // If the rep manually overwrites a suggested field, mark
                  // it as no-longer-suggested so the banner copy stays honest.
                  setAutoSuggested((prev) => ({
                    duration: prev.duration && next.goal_duration_weeks === conditions.goal_duration_weeks,
                    hypercare: prev.hypercare && next.hypercare_weeks === conditions.hypercare_weeks,
                  }));
                  setConditions(next);
                }}
                estimateId={null}
                salesLead={salesLead}
                entityContext={{
                  entities: wizEntities,
                  entityId: wizEntityId,
                  region: wizRegion,
                  closeDate: wizCloseDate,
                  onCloseDateChange: setWizCloseDate,
                  onChange: (eid, r) => {
                    if (eid !== wizEntityId) {
                      setWizEntityId(eid);
                      const en = wizEntities.find(x => x.id === eid);
                      const regions = en ? (en.regions?.length ? en.regions : (en.region ? [en.region] : [])) : [];
                      setWizRegion(regions.length === 1 ? regions[0] : null);
                    } else {
                      setWizRegion(r);
                    }
                  },
                }}
                onSalesLeadChange={setSalesLead}
                csLead={csLead}
                onCSLeadChange={setCsLead}
              />
            </>
          )}
          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-step-4">
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-[#4ABFCF]" /> Product pricing (optional)</CardTitle>
                  <p className="text-xs text-slate-500">Price the platform subscription for this customer. It is saved with the estimate once you save it in the Estimator, and can be refined any time from the workspace's Product Pricing tab. Leave active users blank to skip.</p>
                </CardHeader>
                <CardContent>
                  <ProductPricingCalculator
                    initialInputs={productPricing.inputs}
                    entity={wizEntities.find((x) => x.id === wizEntityId) || null}
                    suggested={{ active_users: targetAudience ? parseInt(targetAudience, 10) : null, private_cloud: (hostingModel || '').toLowerCase().startsWith('private'), currency: wizEntities.find((x) => x.id === wizEntityId)?.default_currency_code || null }}
                    onChange={(inputs, preview) => setProductPricing({ inputs, preview })}
                  />
                </CardContent>
              </Card>
            </div>
          )}
          {step === 5 && (
            <ReviewStep
              productPricing={productPricing.preview && Number(productPricing.inputs?.quantities?.active_users) > 0 ? productPricing.preview : null}
              orgName={orgName}
              implName={implName}
              implDescription={implDescription}
              targetAudience={targetAudience}
              hostingModel={hostingModel}
              conditions={conditions}
              entityLabel={(() => {
                const en = wizEntities.find(x => x.id === wizEntityId);
                if (!en) return null;
                return `${(en.code || en.id).toUpperCase()}${wizRegion ? ` · ${wizRegion}` : ''}${en.default_currency_code ? ` · ${en.default_currency_code}` : ''}`;
              })()}
            />
          )}
        </div>

        <div className="mt-8 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleBack}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            onClick={handleContinue}
            className="bg-[#0F2040] hover:bg-[#0F2040]/90 text-white"
            data-testid="wizard-continue-btn"
          >
            {step === 5 ? 'Continue to Configure' : step === 4 && !(Number(productPricing.inputs?.quantities?.active_users) > 0) ? 'Skip' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-center" data-testid="wizard-step-indicator">
      {STEPS.map((s, i) => {
        const isCurrent = step === s.id;
        const isDone = step > s.id;
        return (
          <div key={s.id} className="flex items-center">
            <div className="flex flex-col items-center min-w-[120px]">
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  isDone
                    ? 'bg-[#0F2040] text-white'
                    : isCurrent
                      ? 'bg-[#4ABFCF] text-white ring-4 ring-[#4ABFCF]/20 scale-105'
                      : 'bg-slate-100 text-slate-400 border-2 border-slate-200'
                }`}
                data-testid={`wizard-step-bubble-${s.id}`}
              >
                {isDone ? <Check className="w-5 h-5" /> : s.id}
              </div>
              <div className={`text-xs mt-2 font-semibold ${
                isCurrent ? 'text-[#4ABFCF]' : isDone ? 'text-[#0F2040]' : 'text-slate-400'
              }`}>
                {s.label}
              </div>
              <div className="text-[10px] text-slate-500">{s.desc}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-12 lg:w-20 h-0.5 mx-1 -mt-6 rounded-full transition-all duration-500 ${
                step > s.id ? 'bg-[#0F2040]' : 'bg-slate-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1Customer({
  organizations, orgsLoading, selectedOrgId, onPickOrg,
  orgName, setOrgName,
  implName, setImplName,
  implDescription, setImplDescription,
  targetAudience, setTargetAudience,
  hostingModel, setHostingModel,
}) {
  return (
    <div className="space-y-6" data-testid="wizard-step-1">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#4ABFCF]" />
            Customer Organization
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Pick an existing customer or type a new name to create one on save.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Existing Customer (optional)</Label>
            <Select
              value={selectedOrgId || 'NONE'}
              onValueChange={(v) => onPickOrg(v === 'NONE' ? '' : v)}
              disabled={orgsLoading}
            >
              <SelectTrigger className="mt-1" data-testid="wizard-org-select">
                <SelectValue placeholder={orgsLoading ? 'Loading…' : 'Choose an existing customer'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">— Create new on save —</SelectItem>
                {organizations.map((o) => (
                  <SelectItem key={o.org_id} value={o.org_id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="wiz-org-name" className="text-xs font-semibold text-slate-700">
              Customer Name <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="wiz-org-name"
              data-testid="wizard-org-name-input"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="e.g. Acme Corporation"
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#4ABFCF]" />
            Project Basics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="wiz-impl-name" className="text-xs font-semibold text-slate-700">
              Project Name <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="wiz-impl-name"
              data-testid="wizard-impl-name-input"
              value={implName}
              onChange={(e) => setImplName(e.target.value)}
              placeholder="e.g. Q3 LMS Rollout"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="wiz-impl-desc" className="text-xs font-semibold text-slate-700">
              Description (optional)
            </Label>
            <Textarea
              id="wiz-impl-desc"
              data-testid="wizard-impl-desc-input"
              value={implDescription}
              onChange={(e) => setImplDescription(e.target.value)}
              placeholder="What's the business driver?"
              rows={3}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="wiz-audience" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Users className="w-3 h-3" /> Target Audience (users)
              </Label>
              <Input
                id="wiz-audience"
                data-testid="wizard-audience-input"
                type="number"
                min={0}
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="e.g. 5000"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Server className="w-3 h-3" /> Hosting Model
              </Label>
              <Select value={hostingModel} onValueChange={setHostingModel}>
                <SelectTrigger className="mt-1" data-testid="wizard-hosting-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public_cloud">Public Cloud (default)</SelectItem>
                  <SelectItem value="private_cloud">Private Cloud (+ infra hours)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewStep({
  orgName, implName, implDescription, targetAudience, hostingModel, conditions, entityLabel = null, productPricing = null,
}) {
  const c = conditions || EMPTY_CONDITIONS;
  return (
    <div className="space-y-6" data-testid="wizard-step-5">
      {productPricing && (
        <Card className="border-slate-200" data-testid="wizard-review-product-pricing">
          <CardContent className="p-4 flex items-center gap-4 text-sm">
            <Package className="w-4 h-4 text-[#4ABFCF]" />
            <div><span className="font-semibold text-[#0F2040]">{productPricing.package.label}</span> · {productPricing.term_years}-year subscription</div>
            <div className="ml-auto tabular-nums"><span className="text-slate-500 text-xs mr-1">Product TCO</span><span className="font-semibold">A${Math.round(productPricing.totals.net).toLocaleString()}</span>
              {productPricing.totals.margin_pct != null && <span className="text-xs text-slate-500 ml-2">margin {productPricing.totals.margin_pct}%</span>}</div>
          </CardContent>
        </Card>
      )}
      <Card className="border-[#4ABFCF]/30 bg-[#4ABFCF]/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#0F2040]">Ready to Configure</CardTitle>
          <p className="text-xs text-slate-600 mt-1">
            We'll save these details and drop you into the component / factor configuration.
            On save, this estimate will be flagged <span className="font-semibold">has_conditions</span> so the Planner becomes read-only.
          </p>
        </CardHeader>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#4ABFCF]" />
            Customer & Project
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <ReviewRow label="Customer" value={orgName || '—'} />
          <ReviewRow label="Project" value={implName || '—'} />
          {entityLabel && <ReviewRow label="Entity / Region" value={entityLabel} />}
          {implDescription && <ReviewRow label="Description" value={implDescription} />}
          {targetAudience && <ReviewRow label="Target audience" value={`${targetAudience} users`} />}
          <ReviewRow
            label="Hosting"
            value={hostingModel === 'private_cloud' ? 'Private Cloud' : 'Public Cloud'}
            icon={hostingModel === 'private_cloud' ? <Server className="w-3 h-3" /> : <Cloud className="w-3 h-3" />}
          />
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#4ABFCF]" />
            Project Conditions
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <ReviewRow label="Goal duration" value={c.goal_duration_weeks ? `${c.goal_duration_weeks} weeks` : '—'} />
          <ReviewRow label="Target start" value={c.goal_start_date || '—'} />
          <ReviewRow label="Target go-live" value={c.goal_go_live_date || '—'} />
          <ReviewRow label="Hypercare" value={c.hypercare_weeks ? `${c.hypercare_weeks} weeks` : '—'} />
          <ReviewRow label="Holiday region" value={c.holiday_region || '—'} />
          <ReviewRow
            label="Breaks"
            value={(c.breaks || []).length > 0
              ? (
                <div className="flex flex-wrap gap-1.5">
                  {c.breaks.map((b) => (
                    <Badge key={b.id} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                      {b.label || 'Break'} ({b.start_date || '?'} → {b.end_date || '?'})
                    </Badge>
                  ))}
                </div>
              )
              : '—'}
          />
          <ReviewRow
            label="Blackouts"
            value={(c.blackouts || []).length > 0
              ? (
                <div className="flex flex-wrap gap-1.5">
                  {c.blackouts.map((b) => (
                    <Badge key={b.id} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                      {b.label || 'Blackout'} ({b.start_date || '?'} → {b.end_date || '?'})
                    </Badge>
                  ))}
                </div>
              )
              : '—'}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewRow({ label, value, icon }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <div className="text-xs font-semibold text-slate-500 w-32 shrink-0 flex items-center gap-1 pt-0.5">
        {icon}
        {label}
      </div>
      <div className="text-sm text-slate-800 flex-1">{value}</div>
    </div>
  );
}


/**
 * Step2Maturity — read-only maturity readout for the newly-picked customer.
 *
 * v260.117 — Optional. Shows the existing assessment (gauge + band copy +
 * "Assess / Re-assess" CTA into the standalone /maturity page) if one
 * exists; otherwise nudges the rep to take it. Never blocks the wizard.
 */
function Step2Maturity({ orgId, orgName, onInsights }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (!orgId) {
          const empty = { has_assessment: false, missing_org: true };
          if (!cancelled) {
            setInsights(empty);
            onInsights?.(empty);
          }
          return;
        }
        const r = await axios.get(
          `${API_URL}/api/maturity/insights?organization_id=${encodeURIComponent(orgId)}`,
          { withCredentials: true },
        );
        if (!cancelled) {
          setInsights(r.data);
          onInsights?.(r.data);
        }
      } catch {
        const empty = { has_assessment: false };
        if (!cancelled) {
          setInsights(empty);
          onInsights?.(empty);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, onInsights]);

  const openMaturityTab = () => {
    if (!orgId) return;
    window.open(`/maturity?organization_id=${encodeURIComponent(orgId)}`, '_blank');
  };

  return (
    <Card data-testid="wizard-step-maturity">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-500" />
          Organisation Maturity (optional)
        </CardTitle>
        <p className="text-sm text-slate-500">
          Recommended. Scoring {orgName ? <span className="font-semibold">{orgName}</span> : 'the customer'} now
          sets the baseline for Readiness Factors and surfaces the score in the Design Pack.
          You can also do this later from the estimate's <span className="font-semibold">Maturity</span> tab.
        </p>
      </CardHeader>
      <CardContent>
        {!orgId ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            Maturity is scored per Customer Organisation. Go back to Step 1 and pick (or create)
            an organisation, then you'll be able to assess it here.
          </div>
        ) : loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : insights?.has_assessment ? (
          <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="text-4xl font-extrabold text-[#0F2040] tabular-nums">
              {Math.round(insights.overall_score)}
              <span className="text-base font-normal text-slate-500 ml-1">/100</span>
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                Assessment on file · {insights.maturity_band}
              </div>
              <div className="text-sm text-slate-700 mt-0.5">
                Last assessed{' '}
                {insights.created_at ? new Date(insights.created_at).toLocaleDateString() : '—'}.
                {insights.primary_constraint && (
                  <> Primary constraint: <span className="font-semibold">{insights.primary_constraint.name}</span>.</>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={openMaturityTab} data-testid="wizard-maturity-update">
              Update score
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-5 text-center">
            <div className="text-base font-semibold text-[#0F2040]">
              No assessment yet for this customer
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              ~6 minutes, 30 questions. You can always do it later from the
              estimate's Maturity tab — but doing it now keeps everything in one flow.
            </p>
            <Button
              className="mt-3 bg-[#0F2040] hover:bg-[#1a2f5a]"
              onClick={openMaturityTab}
              data-testid="wizard-maturity-take"
            >
              Take assessment in a new tab
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <div className="text-xs text-slate-400 mt-2">Click <b>Next</b> below to skip and continue.</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
